import Box from '@mui/material/Box'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../store'
import { MenuItem, Stack, Typography } from '@mui/material'
import { setSelectedPage } from '../../actions/permit'
import { useContext, useEffect, useMemo } from 'react'
import CustomDropdown from '../CustomDropdown'
import Tools, { toolsGroup5 } from './Tools'
import { fetchCommentsAsync } from '../../services/commentsService'
import { ConstructionTab } from '../../reducers/construction'
import { setIsAddCommentMode, setIsCommentsMode } from '../../actions/comments'
import { commentsCursor } from '../../models/Comment'
import {
  AttributeActionNames,
  AttributeResourceNames,
} from '../../models/AccessControl'
import { AbilityContext, Can } from '../../utils/AbilityUtil'
import CommentsClusterComponent from '../Comments/CommentsClusterComponent'

const pagesFilter = [
  'Architecture',
  'Engineering',
  'Structure',
  'Mechanical',
  'Electrical',
  'Plumbing',
  'Civil',
]

function Permit() {
  const dispatch = useDispatch<any>()

  const permit = useSelector((state: RootState) => state.permit)
  const selectedPageId = permit.selectedPageId
  const activeTool = permit.activeTool
  const file = permit.file
  const comments = useSelector((state: RootState) => {
    const tab = state.comments.commentClusters.find(
      (cluster) => cluster.tab === ConstructionTab.Permit,
    )
    return tab ? tab.comments : []
  })
  const isCommentsMode = useSelector(
    (state: RootState) => state.comments.isCommentsMode,
  )
  const isAddCommentMode = useSelector(
    (state: RootState) => state.comments.isAddCommentMode,
  )
  const selectedFile = useSelector(
    (state: RootState) => state.organization.selectedFile,
  )

  const ability = useContext(AbilityContext)

  const handleImageClick = (pageId: string) => {
    dispatch(setSelectedPage({ pageId }))
  }

  const handleAddCommentClick = () => {
    dispatch(setIsAddCommentMode(!isAddCommentMode))
  }

  useEffect(() => {
    dispatch(
      fetchCommentsAsync({
        tab: ConstructionTab.Permit,
        file_id: selectedFile?.id,
      }),
    )
  }, [dispatch])

  useEffect(() => {
    if (file!.pages?.length > 0) {
      dispatch(setSelectedPage({ pageId: file!.pages[0].id }))
    }
  }, [file, dispatch])

  const selectedPageImage = useMemo(() => {
    return file?.pages.find((page) => page.id === selectedPageId)?.image
  }, [file, selectedPageId])

  useEffect(() => {
    dispatch(setIsCommentsMode(activeTool === toolsGroup5.tools[1].name))
    dispatch(setIsAddCommentMode(false))

    return () => {
      dispatch(setIsCommentsMode(false))
    }
  }, [activeTool])

  return (
    <Can I={AttributeActionNames.Read} a={AttributeResourceNames.Permit}>
      <Box
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          background: '#202020',
          padding: '19px 11px 11px 11px',
          boxSizing: 'border-box',
        }}
      >
        <Box
          sx={{
            width: '257px',
            borderRadius: '4px',
            background: '#353535',
            boxShadow: '0px 3px 5px 0px rgba(47, 47, 47, 0.25)',
            padding: '8px 13px',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <CustomDropdown
            title={pagesFilter[0]}
            color='#fff'
            buttonStyleProps={{
              justifyContent: 'space-between',
              width: 'calc(100% - 26px)',
              padding: '0.3rem 0.6rem',
            }}
            titleStyleProps={{
              fontSize: '0.7rem',
            }}
          >
            {pagesFilter.map((filter, index) => (
              <MenuItem key={index} onClick={() => {}}>
                <Box
                  sx={{
                    width: '60%',
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '0.64rem',
                      fontStyle: 'normal',
                      fontWeight: 500,
                      lineHeight: 'normal',
                    }}
                  >
                    {filter}
                  </Typography>
                </Box>
              </MenuItem>
            ))}
          </CustomDropdown>
          <Stack
            sx={{
              flexGrow: 1,
              overflowY: 'auto',
              mt: '10px',
            }}
            spacing='10px'
          >
            {file?.pages.map((page, index) => (
              <img
                key={index}
                src={page.image}
                alt='Permit page'
                onClick={() => handleImageClick(page.id)}
                style={{
                  borderRadius: '6.869px',
                  boxShadow: '0px 2.747px 2.747px 0px rgba(0, 0, 0, 0.25)',
                  border:
                    selectedPageId === page.id
                      ? '0.778px solid #3F69FF'
                      : '0.778px solid #CFCFCF',
                }}
              ></img>
            ))}
          </Stack>
        </Box>
        <Box
          sx={{
            flexGrow: 1,
            height: '100%',
            margin: '0 21px 0 9px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Box
            onClick={handleAddCommentClick}
            sx={{
              flexGrow: 1,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              width: '100%',
              height: 'calc(100% - 53px)',
              cursor: isCommentsMode
                ? `url(${commentsCursor}) 0 20, auto`
                : 'default',
            }}
          >
            <img
              src={selectedPageImage}
              alt='Active permit page'
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'fill',
                borderRadius: '4px',
              }}
            />
          </Box>
          <Box
            sx={{
              height: '44px',
              borderRadius: '11.579px',
              background: '#353535',
              boxShadow: '0px 3.474px 5.789px 0px rgba(47, 47, 47, 0.25)',
              marginTop: '9px',
              padding: '6px',
            }}
          >
            <Tools
              disabled={ability.cannot(
                AttributeActionNames.Manage,
                AttributeResourceNames.Permit,
              )}
            />
          </Box>
        </Box>
        <Box
          sx={{
            width: '32px',
          }}
        >
          <CommentsClusterComponent
            tab={ConstructionTab.Permit}
            comments={comments}
            darkMode={true}
          />
        </Box>
      </Box>
    </Can>
  )
}

export default Permit
